import { useState } from "react";

/**
 * This hook provides a store to manage the ordering state of a table.
 * It also provides helper functions for managing the ordering state
 * and getting the ordering icon based on the current ordering state.
 *
 * It syncs the ordering state with the URL search params field `ordering`.
 *
 * @param defaultOrder - The Django model field name that the api will use to order the data
 * prefix with "-" for descending order, e.g. "-updated_at"
 * @returns ordering, setOrdering, handleSortChange, defaultOrder, determineSortIcon
 */
function useOrdering(defaultOrder: string) {
  const [ordering, setOrdering] = useState(defaultOrder);
  const defaultOrderIsDesc = defaultOrder[0] === "-";

  /**
   * toggles ordering state between ascending and descending order
   * @param sortField - The Django model field name that the api will use to order the data
   */
  function handleSortChange(sortField: string) {
    if (ordering === sortField) {
      setOrdering(`-${sortField}`);
    } else if (
      ordering === `-${sortField}` &&
      (defaultOrderIsDesc ? sortField !== defaultOrder.slice(1) : sortField !== defaultOrder)
    ) {
      setOrdering(defaultOrderIsDesc ? defaultOrder : `-${defaultOrder}`);
    } else {
      setOrdering(sortField);
    }
  }

  /**
   * Returns the NDS arrow icon name based on the current ordering state
   * We commonly use this design pattern to indicate the ordering state of a table column.
   * Up arrow: ascending order
   * Down arrow: descending order
   * Up-down arrow: column is not being used for ordering
   * @param sortField - The Django model field name that the api will use to order the data
   */
  function determineSortIcon(sortField: string) {
    if (ordering === sortField) {
      return "arrow-up";
    } else if (ordering === `-${sortField}`) {
      return "arrow-down";
    } else {
      return "arrow-up-down";
    }
  }

  return { ordering, setOrdering, handleSortChange, defaultOrder, determineSortIcon };
}

export default useOrdering;
